import { AGENT_511, SESSION_ENDED } from '@rsos/constants/agent511';
import { getMostRecentLocations } from '@rsos/utils/getMostRecentLoc';

const ALERT_MAX_AGE = 43200000; // 12 hours in milliseconds
const EXTRA_INFO = 'extra_info';
const META = 'meta';

export const isExtraInfoOrMeta = location => {
  const extraInfoOrMeta = location?.extra_info ? EXTRA_INFO : META;
  return {
    payloadID: location?.[extraInfoOrMeta]?.payload_id || null,
    requestID: location?.[extraInfoOrMeta]?.request_id || null,
    agent511SessionID:
      location?.[extraInfoOrMeta]?.agent_511_session_id || null,
  };
};

/**
 * Checks if a call in the 911 queue is still active.
 * @param {object} call - the call object in the 911 queue
 * @param {string} selectedCallerID - the selected caller ID for the 911 queue
 * @param {boolean} hasAgent511Access - does user have access to view Agent511 text-to-911 requests?
 */
export const shouldRemoveCallFromQueue = (
  call,
  selectedCallerID,
  hasAgent511Access,
) => {
  const nowTS = Math.round(new Date().getTime());

  const { mostRecentLocation, mostRecentSecondaryLocation } =
    getMostRecentLocations(call);
  const callerID =
    mostRecentLocation?.caller_id || mostRecentSecondaryLocation?.caller_id;

  const isCallInactive =
    nowTS > call.expiredTime && selectedCallerID !== callerID;

  if (hasAgent511Access) {
    const tt911ADR = call?.additionalData?.[AGENT_511];
    const tt911SessionEnded = !!(
      tt911ADR?.[0]?.data?.subtype === SESSION_ENDED
    );

    // If a call is a text-to-911 request, it can only be removed when backend sends a
    // Hermes message signaling that the text-to-911 session is terminated.
    if (call.isTT911 && tt911SessionEnded && callerID !== selectedCallerID)
      return true;

    // If a call is NOT a text-to-911 request, it will be removed from the queue if it is
    // expired and not selected.
    if (!call.isTT911 && isCallInactive) return true;
  } else if (isCallInactive) return true;

  return false;
};

/**
 * Checks if the alert in the alerts queue is still active.
 * @param {object} alert - the alert object in the alerts queue
 * @param {string} selectedAlertID - the selected alert ID for the alerts queue
 */
export const shouldRemoveAlertFromQueue = (alert, selectedAlertID) => {
  const nowTS = Math.round(new Date().getTime());
  const expiredTime = alert.created_time + ALERT_MAX_AGE;

  if (nowTS > expiredTime && selectedAlertID !== alert.alert_id) return true;

  return false;
};

/**
 * Checks if the spatial layer is active, if not deactivate it.
 * @param {array} spatialLayer - spatial layer object
 */
export const isSpatialLayerActive = spatialLayer => {
  const nowTS = Math.round(new Date().getTime());

  if (nowTS > spatialLayer.expiredTime) return true;

  return false;
};

/**
 * Checks if the 911 queue size exceeds the max queue size (currently capped at 50), if so then
 * remove the non-selected call with the oldest expiration time.
 * If an agency has text-to-911 feature enabled, remove the non-text-to-911 request with the oldest
 * expiration time first, if all of the requests in the queue are text-to-911 requests, then remove
 * the text-to-911 request with the oldest expiration time.
 * @param {object} callQueue - list of calls in the 911 queue
 * @param {string} selectedCallerID - the selected caller ID
 * @param {function} removeCallFromQueue - function to remove a call from the queue
 */
export const check911QueueExceedsMaxSize = (
  callQueue,
  selectedCallerID,
  removeCallFromQueue,
) => {
  let oldestCall, oldestTT911;

  for (const key in callQueue) {
    if (selectedCallerID !== key) {
      if (callQueue[key].isTT911) {
        oldestTT911 =
          !oldestTT911 || callQueue[key].expiredTime < oldestTT911.expiredTime
            ? {
                callerID: key,
                expiredTime: callQueue[key].expiredTime,
              }
            : oldestTT911;
      } else {
        oldestCall =
          !oldestCall || callQueue[key].expiredTime < oldestCall.expiredTime
            ? {
                callerID: key,
                expiredTime: callQueue[key].expiredTime,
              }
            : oldestCall;
      }
    }
  }

  if (oldestCall && callQueue[oldestCall.callerID]) {
    removeCallFromQueue(oldestCall.callerID);
  } else if (oldestTT911 && callQueue[oldestTT911.callerID]) {
    removeCallFromQueue(oldestTT911.callerID);
  }
};
